import {ResultActionDialogComponent} from '../event-results/result-action-dialog.component';
import { GeneralYesNoDialogComponent } from '../dialog/general-yes-no-dialog/general-yes-no-dialog.component';
import { firstValueFrom } from 'rxjs';
import { AttendanceDraftTableComponent } from './attendance-draft-table.component';
import { AttendanceTargetTableComponent } from './attendance-target-table.component';
import { AttendanceSummaryComponent } from './attendance-summary.component';
import { AttendanceHistoryComponent } from './attendance-history.component';
import { AttendanceShareComponent } from './attendance-share.component';
import { AttendanceSetupFormComponent } from './attendance-setup-form.component';
import { ActualStatus, AttendanceTarget, RoleItem, AttendanceConfiguration, SetupPreview, FinalizationInfo, FinalizationPreview, AddCandidate, Dashboard } from './attendance.types';
import { AttendanceApiService } from './attendance-api.service';
import { CommonModule } from '@angular/common';
import { EventOperationApiService } from '../event-operation-settings/event-operation-api.service';
import { A11yModule } from '@angular/cdk/a11y';
import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ApproverSelectDialogComponent } from '../dialog/approver-select-dialog/approver-select-dialog.component';
import { Approver } from '../model/my-event';
import { Constant } from '../constant';
import { AttendanceReasonInputComponent, ReasonContext } from './attendance-reason-input.component';

@Component({
  selector: 'app-attendance-management',
  standalone: true,
  imports: [ResultActionDialogComponent,CommonModule, FormsModule, MatButtonModule, MatDialogModule, MatProgressSpinnerModule, A11yModule, AttendanceReasonInputComponent, AttendanceSetupFormComponent, AttendanceShareComponent, AttendanceHistoryComponent, AttendanceSummaryComponent, AttendanceTargetTableComponent, AttendanceDraftTableComponent],
  templateUrl: './attendance-management.component.html',
  styleUrls: ['../event-results/result-actions.css', './attendance-common.css', './attendance-management.component.css'],
})
export class AttendanceManagementComponent implements OnInit, OnDestroy {
  dashboard?: Dashboard;
  canOpenReport = false;
  selectedDate: string | null = null;
  setup?: SetupPreview;
  setupReady = false;
  private savedSetupValue = '';
  private savedDates: string[] = [];
  answerDate = ''; answerTime = ''; checkinDate = ''; checkinTime = '';
  showSpinner = true;
  errorMessage = '';
  selected = new Set<number>();
  draftSelected = new Set<string>();
  addCandidates: AddCandidate[] = [];
  addReason = '';
  reasonOpen = false;
  reason = '';
  reasonTitle = '';
  targetScope: 'day' | 'all' = 'day';
  pendingLifecycle?: 'cancel' | 'postpone' | 'resume' | 'correct';
  finalization?: FinalizationInfo;
  finalPreview?: FinalizationPreview;
  finalizeOpen = false;
  finalizationBusy = false;
  finalizationError = '';
  pendingActual?: { target: AttendanceTarget; next: ActualStatus; select: HTMLSelectElement };
  eventId = 0;
  searchText = '';
  statusFilter: 'all' | ActualStatus = 'all';
  organizationFilter = 'all';
  sortOrder: 'organization-asc' | 'organization-desc' | 'name-asc' | 'name-desc' | 'status-asc' | 'status-desc' = 'organization-asc';
  private refreshTimer?: ReturnType<typeof setTimeout>;
  refreshInProgress = false;
  private dashboardRequestVersion = 0;
  private destroyed = false;

  exportBusy=false;
  async exportAll(){if(this.exportBusy)return;this.exportBusy=true;try{const res=await this.api.request<any>(this.eventId,'/myevent/attendance/admin',{action:'export'});const data=res.data;const bytes=Uint8Array.from(atob(data.content),(c)=>c.charCodeAt(0));const url=URL.createObjectURL(new Blob([bytes],{type:data.mimeType}));const a=document.createElement('a');a.href=url;a.download=data.fileName;a.click();setTimeout(()=>URL.revokeObjectURL(url),1000);}catch(e:any){this.finalizationError=e?.message||'出欠データを出力できませんでした。';}finally{this.exportBusy=false;}}
  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private api: AttendanceApiService,
    private dialog: MatDialog,
    private operations: EventOperationApiService,
  ) {}

  ngOnInit(): void {
    this.eventId = Number(this.route.snapshot.paramMap.get('id'));
    this.operations.get(this.eventId).then(settings => {
      if (!this.destroyed) this.canOpenReport = settings.capabilities.canConfigure;
    }).catch(() => { this.canOpenReport = false; });
    this.load();
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    ++this.dashboardRequestVersion;
    clearTimeout(this.refreshTimer);
  }

  get refreshIntervalSeconds(): number { return this.dashboard?.quorum ? 15 : 60; }

  private scheduleRefresh(): void {
    clearTimeout(this.refreshTimer);
    if (this.destroyed || !this.dashboard) return;
    this.refreshTimer = setTimeout(() => {
      if (!document.hidden && !this.showSpinner && !this.refreshInProgress && !this.reasonOpen && !this.finalizeOpen &&
          !this.finalizationBusy && !this.addCandidates.length && !this.selected.size && !this.dialog.openDialogs.length) {
        this.load(true);
      } else {
        this.scheduleRefresh();
      }
    }, this.refreshIntervalSeconds * 1000);
  }

  back(): void {
    this.openDocument('plan');
  }

  get isOperationEntry(): boolean {
    return this.route.snapshot.queryParamMap.get('entry') === 'operations';
  }

  openDocument(tab: 'plan' | 'report'): void {
    if (tab === 'report' && !this.canOpenReport) return;
    this.router.navigate([this.route.snapshot.queryParamMap.get('entry') === 'event' ? '/event' : '/myevent-detail', this.eventId], {
      queryParams: {
        ...(this.route.snapshot.queryParamMap.get('from') ? { from: this.route.snapshot.queryParamMap.get('from') } : {}),
        tab,
      },
    });
  }

  toggle(targetId: number, checked: boolean): void {
    checked ? this.selected.add(targetId) : this.selected.delete(targetId);
  }

  private setSetup(data: SetupPreview): void {
    clearTimeout(this.refreshTimer);
    this.setup = data;
    this.dashboard = undefined;
    this.draftSelected.clear();
    [this.answerDate, this.answerTime] = data.settings.answerDeadline.replace(' ', 'T').slice(0,16).split('T');
    [this.checkinDate, this.checkinTime] = data.settings.checkinStart.replace(' ', 'T').slice(0,16).split('T');
    this.setupReady = data.status === 'ready' && !data.scheduleNotice;
    this.savedSetupValue = this.setupValue;
    this.savedDates = data.configuration.sessions.map(s => s.start.slice(0,10));
    // 旧方式は設定の保存時に日別方式へ移行する。保存前には実績を書き換えない。
    data.configuration.attendanceMode = 'daily';
    this.clearMultiDayQuorum();
  }

  private clearMultiDayQuorum(): void {
    if (this.setup && this.setup.configuration.sessions.length > 1) {
      Object.assign(this.setup.settings, {quorumMode:'none',quorumTotal:null,quorumRequired:null,quorumRoleIds:[]});
    }
  }

  get setupOpened(): boolean { return !!this.setup && (!!this.setup.rosterFrozen || ['open','closed','correcting'].includes(this.setup.status)); }
  get invalidDraftTargets(): boolean { return !!this.setup?.targets.some(p => !!p.validationError); }
  get statusLabel(): string { return ({open:'受付中',closed:'回答受付終了',finalized:'実績確定済み',cancelled:'中止',postponed:'延期',correcting:'実績修正中'} as Record<string,string>)[this.dashboard?.status || ''] || ''; }

  private get setupValue(): string { return JSON.stringify([this.setup?.configuration, this.setup?.settings, this.answerDate, this.answerTime]); }
  get setupChanged(): boolean { return this.setupValue !== this.savedSetupValue; }
  get additionRoles(): RoleItem[] { return (this.setup?.roleOptions || this.dashboard?.roles || []).filter(r => r.enabled !== false); }
  get additionsValid(): boolean { return !!this.addCandidates.length && !!this.addReason.trim() && this.addCandidates.every(p => p.roleId !== null); }

  get displayedTargets(): AttendanceTarget[] {
    const query = this.searchText.trim().toLocaleLowerCase('ja-JP');
    const statusRank: Record<ActualStatus, number> = { not_checked_in: 0, attended: 1, absent: 2 };
    const filtered = (this.dashboard?.targets || []).filter(target => {
      const searchable = [target.name, target.organizationPath, target.role, target.operatorUserId]
        .filter(Boolean).join(' ').toLocaleLowerCase('ja-JP');
      return (!query || searchable.includes(query)) &&
        (this.statusFilter === 'all' || target.actualStatus === this.statusFilter) &&
        (this.organizationFilter === 'all' || target.organizationGroupId === this.organizationFilter);
    });
    const [field, direction] = this.sortOrder.split('-') as ['organization' | 'name' | 'status', 'asc' | 'desc'];
    const sign = direction === 'asc' ? 1 : -1;
    const organizationRank = new Map(
      (this.dashboard?.organizationSummary || []).map((item, index) => [item.organizationId, index]),
    );
    return filtered.sort((left, right) => {
      let compared = 0;
      if (field === 'status') compared = statusRank[left.actualStatus] - statusRank[right.actualStatus];
      else if (field === 'name') compared = left.name.localeCompare(right.name, 'ja');
      else compared = (organizationRank.get(left.organizationGroupId || '') ?? Number.MAX_SAFE_INTEGER)
        - (organizationRank.get(right.organizationGroupId || '') ?? Number.MAX_SAFE_INTEGER);
      if (compared === 0) compared = left.targetId - right.targetId;
      return compared * sign;
    });
  }

  get hierarchyFilterLabel(): string {
    return this.dashboard?.organizationSummary.length ? '対象組織の一階層下' : '組織';
  }

  remaining(attended: number, required: number): number { return Math.max(0, required - attended); }
  toggleDraft(id: string, checked: boolean): void { checked ? this.draftSelected.add(id) : this.draftSelected.delete(id); }
  selectAllDraft(checked: boolean): void { this.draftSelected = new Set(checked ? this.setup?.targets.map(t => t.operator_user_id) : []); }
  excludeDraft(): void {
    if (!this.setupReady || this.showSpinner) return;
    if (!this.draftSelected.size) return;
    this.reasonTitle = `${this.draftSelected.size}人を出欠対象から除外`;
    this.pendingActual = undefined; this.reason = ''; this.reasonOpen = true;
  }
  private async adjustDraft(operation: 'add'|'exclude', targets: Array<{operatorUserId: string; roleId?: number | null}>, reason: string): Promise<boolean> {
    if (!this.setup || !this.setupReady || this.showSpinner) return false;
    this.showSpinner = true; this.errorMessage = '';
    try {
      const res = await this.finalizationRequest('/myevent/attendance/setup', {
        action: 'adjust_targets', operation, targets, reason,
        settingsRevision: this.setup.settingsRevision, rosterHash: this.setup.rosterHash,
      });
      this.setSetup(res.data);
      return true;
    } catch (err: any) { this.errorMessage = err?.message || '対象者を変更できませんでした。'; return false; }
    finally { this.showSpinner = false; }
  }

  cancelSettings(): void { this.setup=undefined; this.errorMessage=''; this.load(); }
  async editSettings(): Promise<void> {
    const requestVersion = ++this.dashboardRequestVersion;
    clearTimeout(this.refreshTimer);
    this.refreshInProgress = false;
    this.showSpinner=true;
    try {
      const res=await this.finalizationRequest('/myevent/attendance/setup',{action:'configure'});
      if (this.destroyed || requestVersion !== this.dashboardRequestVersion) return;
      this.setSetup(res.data);
    } catch(err:any) {
      if (!this.destroyed && requestVersion === this.dashboardRequestVersion) this.errorMessage=err?.message || '設定を取得できませんでした。';
    } finally {
      if (!this.destroyed && requestVersion === this.dashboardRequestVersion) this.showSpinner=false;
    }
  }

  async saveSetup(): Promise<void> {
    if (!this.setup || this.showSpinner || (this.setup.settingsRevision > 0 && !this.setupChanged)) return;
    if (this.setupOpened && this.savedDates.some(day => !this.setup!.configuration.sessions.some(s => s.start.slice(0,10) === day))) {
      const confirmed = await firstValueFrom(this.dialog.open(GeneralYesNoDialogComponent, {
        data: {msg:'開催日を変更します。旧日程の回答は変更履歴に残し、新しい開催日は未回答から始めます。保存しますか？',check:false},
      }).afterClosed());
      if (!confirmed || this.destroyed || this.showSpinner) return;
    }
    this.showSpinner = true;
    this.errorMessage = '';
    try {
      const res = await this.finalizationRequest('/myevent/attendance/setup', {
        ...this.setup.settings, action: 'save_configuration', settingsRevision: this.setup.settingsRevision,
        configuration: this.setup.configuration,
        conditionHash: this.setup.conditionHash,
        answerDeadline: `${this.answerDate}T${this.answerTime}`,
        checkinStart: `${this.checkinDate}T${this.checkinTime}`,
      });
      const opened=this.setupOpened;
      this.setSetup(res.data);
      if (opened) { this.setup=undefined; this.dashboard=undefined; this.selectedDate=''; this.load(); }
    } catch (err: any) { this.errorMessage = err?.message || '設定を保存できませんでした。'; }
    finally { this.showSpinner = false; }
  }

  async startReception(): Promise<void> {
    if (!this.setup || !this.setupReady || this.showSpinner) return;
    this.showSpinner = true;
    this.errorMessage = '';
    try {
      await this.finalizationRequest('/myevent/attendance/setup', {
        action: 'start', settingsRevision: this.setup.settingsRevision,
        conditionHash: this.setup.conditionHash, rosterHash: this.setup.rosterHash,
      });
      this.setup = undefined;
      this.load();
    } catch (err: any) {
      this.errorMessage = err?.message || '受付を開始できませんでした。';
      this.showSpinner = false;
      this.setupReady = false;
    }
  }

  get releaseReadOnly(): boolean {
    return (this.setup || this.dashboard)?.releaseControl?.canWrite !== true;
  }

  get releaseMessage(): string {
    return (this.setup || this.dashboard)?.releaseControl?.message || '出欠機能は現在、閲覧のみ利用できます。回答や変更は保存できません。';
  }

  get editingLocked(): boolean {
    return this.releaseReadOnly || this.showSpinner || this.finalizationBusy ||
      ['finalized', 'cancelled', 'postponed'].includes(this.dashboard?.status || '') || !!this.dashboard?.operation?.reportStop;
  }

  async prepareFinalization(): Promise<void> {
    if (this.editingLocked || this.addCandidates.length || this.reasonOpen) return;
    this.finalizationBusy = true;
    this.finalizationError = '';
    try {
      const res = await this.finalizationRequest('/myevent/attendance/finalize', { action: 'preview' });
      if (res.data.finalization && !res.data.correctionPending) {
        this.finalization = res.data.finalization;
        this.load();
      } else {
        this.finalPreview = res.data.preview;
        this.finalizeOpen = true;
      }
    } catch (err: any) {
      this.finalizationError = err?.message || '確定前の集計を取得できませんでした。';
    } finally {
      this.finalizationBusy = false;
    }
  }

  async confirmFinalization(): Promise<void> {
    if (!this.finalPreview || this.finalizationBusy) return;
    this.finalizationBusy = true;
    this.finalizationError = '';
    try {
      const res = await this.finalizationRequest('/myevent/attendance/finalize', {
        action: 'finalize', snapshotHash: this.finalPreview.snapshotHash,
      });
      this.finalization = res.data;
      this.finalizeOpen = false;
      this.finalPreview = undefined;
      this.load();
    } catch (err: any) {
      this.finalizationError = err?.message || '実績を確定できませんでした。集計を再確認してください。';
    } finally {
      this.finalizationBusy = false;
    }
  }

  async retryTransfer(): Promise<void> {
    if (this.finalizationBusy) return;
    this.finalizationBusy = true;
    this.finalizationError = '';
    try {
      const res = await this.finalizationRequest('/myevent/attendance/sync', {});
      this.finalization = res.data;
    } catch (err: any) {
      this.finalizationError = err?.message || '転記状態を確認できませんでした。';
    } finally {
      this.finalizationBusy = false;
    }
  }

  get transferMessage(): string {
    if(this.finalization?.reportSync){
      switch(this.finalization.reportSync.state){
        case 'applied':return '参加実績を報告書へ反映し、Excelを添付しました。';
        case 'awaiting_finalization':return 'アンケート集計の確定後に、報告書への反映が完了します。';
        case 'pending':return '報告書が確認中・完了のため、反映を待っています。';
        case 'failed':case 'retry_required':return '実績は確定済みです。Excel添付を含む報告書への反映を再試行してください。';
      }
    }
    switch (this.finalization?.syncState) {
      case 'applied': return '既存の報告書へ確定実出席人数を反映しました。';
      case 'held': return '報告書が確認中または完了のため、反映を保留しています。訂正手続き後に再確認してください。';
      case 'failed': return '報告書への転記に失敗しました。出欠実績は確定済みです。再試行してください。';
      default: return '報告書作成待ちです。作成画面に確定実出席人数が自動表示され、報告書の保存時に反映されます。';
    }
  }

  private finalizationRequest<T = any>(path: string, body: object): Promise<{data: T}> {
    const action = (body as {action?: string}).action;
    const isRead = path === '/myevent/attendance/admin' ||
      (path === '/myevent/attendance/setup' && ['preview', 'configure', 'search'].includes(action || 'preview')) ||
      (path === '/myevent/attendance/finalize' && (!action || action === 'preview'));
    if (!isRead && this.releaseReadOnly) return Promise.reject(new Error(this.releaseMessage));
    return this.api.request<T>(this.eventId, path, body);
  }

  requestActual(target: AttendanceTarget, next: ActualStatus, select: HTMLSelectElement): void {
    if (this.editingLocked || !this.dashboard?.canUpdateActual) return;
    if (next === target.actualStatus) return;
    this.pendingActual = { target, next, select };
    this.reasonTitle = `${this.selectedDate ? this.dayLabel(this.selectedDate) + ' ／ ' : ''}${target.name}さんの当日実績を「${this.actualLabel(next)}」へ変更`;
    this.reason = '';
    this.reasonOpen = true;
  }

  cancelReason(): void {
    if (this.pendingActual) this.pendingActual.select.value = this.pendingActual.target.actualStatus;
    this.pendingActual = undefined;
    this.pendingLifecycle = undefined;
    this.reasonOpen = false;
    this.reason = '';
  }

  saveActual(): void {
    if (!this.pendingActual || !this.reason.trim()) return;
    const pending = this.pendingActual;
    this.submit('/myevent/attendance/actual', {
      targetId: pending.target.targetId,
      actualStatus: pending.next,
      attendanceDate: this.selectedDate,
      recordRevision: pending.target.recordRevision,
      reason: this.reason.trim(),
      idempotencyKey: this.idempotencyKey(),
    }, 'put');
    this.reasonOpen = false;
    this.pendingActual = undefined;
  }

  excludeSelected(): void {
    if (!this.dashboard || !this.selected.size) return;
    this.reasonTitle = `${this.selected.size}人を出欠対象から除外`;
    this.targetScope = 'day';
    this.reason = '';
    this.pendingActual = undefined;
    this.reasonOpen = true;
  }

  async saveExclusion(): Promise<void> {
    if (this.setup && !this.pendingActual && this.reason.trim()) {
      const saved = await this.adjustDraft('exclude', [...this.draftSelected].map(operatorUserId => ({operatorUserId})), this.reason);
      if (saved) this.cancelReason();
      return;
    }
    if (!this.dashboard || this.pendingActual || !this.reason.trim()) return;
    this.submit('/myevent/attendance/targets/exclude', {
      targetIds: Array.from(this.selected),
      rosterRevision: this.dashboard.rosterRevision,
      reason: this.reason.trim(),
      attendanceDate: this.targetScope === 'day' ? this.selectedDate : null,
    });
    this.reasonOpen = false;
  }

  openAddDialog(): void {
    if ((!this.dashboard && !this.setupReady) || this.editingLocked) return;
    this.targetScope = 'day';
    const ref = this.dialog.open(ApproverSelectDialogComponent, {
      width: '760px', autoFocus: false,
      data: {
        dialogTitle: '出欠対象者を追加', eventId: this.eventId, documentType: 'plan', attendanceMode: true,
        selectedApprovers: [], excludedUserIds: this.setup ? this.setup.targets.map(item => item.operator_user_id) : this.dashboard!.targets.map(item => item.operatorUserId),
      },
    });
    ref.afterClosed().subscribe((people: Approver[] | false) => {
      if (!people || !people.length) return;
      this.addCandidates = people.map(person => ({
        ...person,
        roleId: person.positionId && this.additionRoles.some(role => role.id === person.positionId)
          ? person.positionId : null,
      }));
      this.addReason = '';
    });
  }

  cancelAdd(): void {
    this.addCandidates = [];
    this.addReason = '';
  }

  async saveAdditions(): Promise<void> {
    if (!this.addCandidates.length || !this.addReason.trim() || this.addCandidates.some(item => item.roleId == null)) return;
    if (this.setup) {
      const saved = await this.adjustDraft('add', this.addCandidates.map(item => ({operatorUserId: item.id, roleId: item.roleId})), this.addReason);
      if (saved) this.cancelAdd();
      return;
    }
    if (!this.dashboard) return;
    this.submit('/myevent/attendance/targets', {
      targets: this.addCandidates.map(item => ({ operatorUserId: item.id, roleId: item.roleId })),
      rosterRevision: this.dashboard.rosterRevision,
      reason: this.addReason.trim(),
      attendanceDate: this.targetScope === 'day' ? this.selectedDate : null,
    });
    this.cancelAdd();
  }

  get correctionBlockedReason(): string {
    if(this.releaseReadOnly)return this.releaseMessage;
    if(this.dashboard?.operation.reportStop)return '報告書が中止・延期のため、修正できません。';
    if(['confirming','completed'].includes(this.dashboard?.operation.reportStatus||''))return '報告書を編集可能な状態に戻してから修正してください。';
    return '';
  }

  requestLifecycle(action: 'cancel' | 'postpone' | 'resume' | 'correct'): void {
    if(action==='correct'&&(this.correctionBlockedReason||this.showSpinner||this.finalizationBusy))return;
    this.errorMessage='';
    this.pendingActual = undefined;
    this.pendingLifecycle = action;
    this.reasonTitle = {cancel:'企画を中止して受付を停止',postpone:'企画を延期して受付を停止',resume:'開催日程を確認して受付を再開',correct:'確定した参加実績の訂正を開始'}[action];
    this.reason = '';
    this.reasonOpen = true;
  }

  get dialogReasonContext(): ReasonContext {
    if (this.pendingLifecycle && this.pendingLifecycle !== 'resume') return this.pendingLifecycle;
    return this.pendingActual ? 'actual' : 'exclude';
  }

  get dialogReasonLabel(): string {
    return this.pendingLifecycle === 'postpone' ? '延期の理由'
      : this.pendingLifecycle === 'cancel' ? '中止の理由' : '変更理由';
  }

  get lifecycleActionLabel(): string {
    return this.pendingLifecycle === 'resume' ? '受付を再開する'
      : this.pendingLifecycle === 'postpone' ? '延期する'
      : this.pendingLifecycle === 'cancel' ? '中止する' : '訂正を開始する';
  }

  async saveLifecycle(): Promise<void> {
    if (this.showSpinner || this.releaseReadOnly || !this.pendingLifecycle || !this.dashboard || (this.pendingLifecycle !== 'resume' && !this.reason.trim())) return;
    this.showSpinner = true;
    try {
      await this.finalizationRequest('/myevent/attendance/setup',{action:'lifecycle',operation:this.pendingLifecycle,
        settingsRevision:this.dashboard.settingsRevision,
        ...(this.pendingLifecycle === 'resume' ? {} : {reason:this.reason.trim()})});
      this.cancelReason();
      this.finalization = undefined;
      this.dashboard = undefined;
      this.selectedDate = '';
      this.showSpinner = false;
      this.load();
    } catch(err: any) { this.errorMessage = err?.message || '開催状況を変更できませんでした。'; this.showSpinner = false; }
  }

  historyTime(value: string): string { return value.replace('T',' ').slice(0,19); }

  actualLabel(status: string): string {
    return status === 'attended' ? '出席' : status === 'absent' ? '欠席' : '未登録';
  }

  dayLabel(day: string): string {
    return new Intl.DateTimeFormat('ja-JP', {year:'numeric',month:'long',day:'numeric',weekday:'short',timeZone:'Asia/Tokyo'}).format(new Date(day+'T00:00:00+09:00'));
  }

  changeAttendanceDate(day: string): void {
    if (this.showSpinner || this.reasonOpen || this.finalizeOpen) return;
    this.selectedDate = day;
    this.selected.clear();
    this.load();
  }

  load(silent = false): void {
    clearTimeout(this.refreshTimer);
    if (this.destroyed || (silent && this.refreshInProgress)) return;
    const requestVersion = ++this.dashboardRequestVersion;
    const requestedDate = this.selectedDate;
    this.refreshInProgress = true;
    if (!silent) {
      this.showSpinner = true;
      this.errorMessage = '';
    }
    const body = requestedDate ? { attendanceDate: requestedDate } : {};
    const request = this.dashboard
      ? this.api.request<Dashboard>(this.eventId, '/myevent/attendance/admin', body)
      : this.api.request<SetupPreview>(this.eventId, '/myevent/attendance/setup', {...body, action:'configure'}).then(preview => {
          if (this.destroyed || requestVersion !== this.dashboardRequestVersion) return null;
          if (['not_configured', 'draft', 'ready'].includes(preview.data.status)) {
            this.setSetup(preview.data);
            return null;
          }
          this.setup = undefined;
          return this.api.request<Dashboard>(this.eventId, '/myevent/attendance/admin', body);
        });
    request.then(res => {
      if (!res || this.destroyed || requestVersion !== this.dashboardRequestVersion) return;
      this.dashboard = res.data;
      this.selectedDate = res.data.attendanceDate;
      if (this.organizationFilter !== 'all' && !res.data.organizationSummary.some(item => item.organizationId === this.organizationFilter)) {
        this.organizationFilter = 'all';
      }
      if (!silent) this.selected.clear();
      if (['finalized','correcting'].includes(this.dashboard?.status || '')) {
        this.finalizationError = '';
        this.finalizationRequest('/myevent/attendance/finalize', { action: 'preview' })
          .then(value => { if (!this.destroyed && requestVersion === this.dashboardRequestVersion) this.finalization = value.data.finalization; })
          .catch(() => { if (!this.destroyed && requestVersion === this.dashboardRequestVersion) this.finalizationError = '確定情報を取得できませんでした。再読み込みしてください。'; });
      }
    }).catch(err => {
      if (!this.destroyed && requestVersion === this.dashboardRequestVersion && !silent) this.errorMessage = err?.error?.message || err?.message || '出欠・参加管理を表示できませんでした。';
    }).finally(() => {
      if (this.destroyed || requestVersion !== this.dashboardRequestVersion) return;
      this.refreshInProgress = false;
      if (!silent) this.showSpinner = false;
      this.scheduleRefresh();
    });
  }

  private submit(path: string, body: object, method: 'post' | 'put' = 'post'): void {
    if (this.releaseReadOnly) { this.errorMessage = this.releaseMessage; return; }
    const requestVersion = ++this.dashboardRequestVersion;
    this.refreshInProgress = false;
    this.showSpinner = true;
    this.errorMessage = '';
    this.api.request<Dashboard>(this.eventId, path, body, method).then(async res => {
      if (this.destroyed || requestVersion !== this.dashboardRequestVersion) return;
      if (this.selectedDate && res.data.attendanceMode === 'daily' && res.data.attendanceDate !== this.selectedDate && res.data.attendanceDates.includes(this.selectedDate)) {
        res = await this.finalizationRequest('/myevent/attendance/admin', {attendanceDate:this.selectedDate});
      }
      if (this.destroyed || requestVersion !== this.dashboardRequestVersion) return;
      this.dashboard = res.data;
      this.selectedDate = res.data.attendanceDate;
      this.selected.clear();
    }).catch(err => {
      if (this.destroyed || requestVersion !== this.dashboardRequestVersion) return;
      this.errorMessage = err?.error?.message || err?.message || '変更できませんでした。再読み込みしてください。';
    }).finally(() => {
      if (this.destroyed || requestVersion !== this.dashboardRequestVersion) return;
      this.showSpinner = false;
      this.scheduleRefresh();
    });
  }

  private idempotencyKey(): string {
    return typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }
}
